import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Flex,
  Icon,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Divider,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { MdAdd } from 'react-icons/md';
import { MastercardIcon, VisaIcon } from 'components/icons/Icons';
import { CreditCardForm } from './CreditCardForm';
import { StripeProvider } from './StripeProvider';
import { stripeService } from 'services/stripeService';
import { useAuth } from 'contexts/AuthContext';
import { StripePaymentMethod } from 'types/stripe';
import { getStripePublishableKey } from 'utils/stripe';
import FirebaseAnalyticsService from 'services/firebaseAnalyticsService';

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriptionId?: string;
  currentPaymentMethod?: StripePaymentMethod;
  onPaymentMethodUpdate?: () => void;
}

export const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  isOpen,
  onClose,
  subscriptionId,
  currentPaymentMethod,
  onPaymentMethodUpdate,
}) => {
  const { token } = useAuth();
  const toast = useToast();
  const [paymentMethods, setPaymentMethods] = useState<StripePaymentMethod[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMethods, setIsLoadingMethods] = useState(false);
  const [showAddNew, setShowAddNew] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<
    string | null
  >(null);
  const [setupIntentLoading, setSetupIntentLoading] = useState(false);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      setIsLoadingMethods(true);
      const methods = await stripeService.getPaymentMethods(token);
      setPaymentMethods(methods);

      // Set current payment method as selected if it exists
      if (currentPaymentMethod) {
        setSelectedPaymentMethod(currentPaymentMethod.id);
      } else if (methods.length > 0) {
        setSelectedPaymentMethod(methods[0].id);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setError('Failed to load payment methods');
    } finally {
      setIsLoadingMethods(false);
    }
  }, [token, currentPaymentMethod]);

  useEffect(() => {
    if (isOpen) {
      fetchPaymentMethods();
    }
  }, [isOpen, fetchPaymentMethods]);

  const createSetupIntent = async () => {
    setSetupIntentLoading(true);
    try {
      // Create a Setup Intent for adding payment methods
      const response = await stripeService.createSetupIntent(token);
      setSetupIntentClientSecret(response.clientSecret);
    } catch (error) {
      console.error('Error creating setup intent:', error);
      setError('Failed to initialize payment form');
    } finally {
      setSetupIntentLoading(false);
    }
  };

  const handleShowAddNew = () => {
    createSetupIntent();
    setShowAddNew(true);
  };

  const handleAddNewPaymentMethod = async (paymentMethodId: string) => {
    try {
      setIsLoading(true);
      FirebaseAnalyticsService.trackFeatureUsage(
        'Billing',
        'add_payment_method_started',
      );

      // Add the new payment method to the customer
      await stripeService.addPaymentMethod(paymentMethodId, token);

      // Refresh the payment methods list
      await fetchPaymentMethods();

      // Set the new payment method as selected
      setSelectedPaymentMethod(paymentMethodId);

      setShowAddNew(false);
      setSetupIntentClientSecret(null);

      FirebaseAnalyticsService.trackFeatureUsage(
        'Billing',
        'add_payment_method_success',
      );
      toast({
        title: 'Payment Method Added',
        description: 'Your new payment method has been added successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error adding payment method:', error);
      FirebaseAnalyticsService.trackFeatureUsage(
        'Billing',
        'add_payment_method_failed',
      );
      toast({
        title: 'Error',
        description:
          error.response?.data?.message || 'Failed to add payment method',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSubscriptionPaymentMethod = async () => {
    if (!subscriptionId || !selectedPaymentMethod) {
      toast({
        title: 'Error',
        description: 'Please select a payment method',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);
      FirebaseAnalyticsService.trackFeatureUsage(
        'Billing',
        'update_payment_method_started',
        { subscription_id: subscriptionId },
      );

      // Update the subscription's payment method
      // Note: This endpoint needs to be implemented on the backend
      await stripeService.updateSubscription(
        subscriptionId,
        selectedPaymentMethod,
        token,
      );

      FirebaseAnalyticsService.trackFeatureUsage(
        'Billing',
        'update_payment_method_success',
        { subscription_id: subscriptionId },
      );
      toast({
        title: 'Payment Method Updated',
        description:
          'Your subscription payment method has been updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onPaymentMethodUpdate?.();
      onClose();
    } catch (error: any) {
      console.error('Error updating payment method:', error);
      FirebaseAnalyticsService.trackFeatureUsage(
        'Billing',
        'update_payment_method_failed',
        { subscription_id: subscriptionId },
      );
      toast({
        title: 'Error',
        description:
          error.response?.data?.message || 'Failed to update payment method',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getCardIcon = (brand: string) => {
    switch (brand.toLowerCase()) {
      case 'visa':
        return <VisaIcon w="24px" h="24px" mt="3px" me="10px" />;
      case 'mastercard':
        return <MastercardIcon w="24px" h="24px" mt="3px" me="10px" />;
      default:
        return <MastercardIcon w="24px" h="24px" mt="3px" me="10px" />;
    }
  };

  const formatExpiryDate = (month: number, year: number) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };

  if (setupIntentLoading) {
    return (
      <Flex justify="center" py={8}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Manage Payment Methods</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertTitle>Error!</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {isLoadingMethods ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <>
                {!showAddNew ? (
                  <>
                    <Text fontSize="lg" fontWeight="bold">
                      Select Payment Method
                    </Text>

                    {paymentMethods.length > 0 ? (
                      <RadioGroup
                        value={selectedPaymentMethod}
                        onChange={setSelectedPaymentMethod}
                      >
                        <Stack spacing={3}>
                          {paymentMethods.map((method) => (
                            <Box
                              key={method.id}
                              border="1px"
                              borderColor={
                                selectedPaymentMethod === method.id
                                  ? 'brand.400'
                                  : 'gray.300'
                              }
                              borderRadius="md"
                              p={3}
                              cursor="pointer"
                              onClick={() =>
                                setSelectedPaymentMethod(method.id)
                              }
                            >
                              <HStack align="center">
                                <Radio value={method.id} />
                                <Flex align="center" flex={1}>
                                  {method.card &&
                                    getCardIcon(method.card.brand)}
                                  <Text fontSize="md" fontWeight="500">
                                    •••• •••• •••• {method.card?.last4}
                                  </Text>
                                  <Text fontSize="sm" color="gray.500" ml={2}>
                                    Expires{' '}
                                    {method.card &&
                                      formatExpiryDate(
                                        method.card.exp_month,
                                        method.card.exp_year,
                                      )}
                                  </Text>
                                </Flex>
                              </HStack>
                            </Box>
                          ))}
                        </Stack>
                      </RadioGroup>
                    ) : (
                      <Text color="gray.500" textAlign="center" py={4}>
                        No payment methods found
                      </Text>
                    )}

                    <Divider />

                    <Button
                      leftIcon={<Icon as={MdAdd} />}
                      variant="outline"
                      onClick={handleShowAddNew}
                      w="full"
                    >
                      Add New Payment Method
                    </Button>
                  </>
                ) : (
                  <>
                    <Text fontSize="lg" fontWeight="bold">
                      Add New Payment Method
                    </Text>
                    <StripeProvider
                      publishableKey={getStripePublishableKey()}
                      clientSecret={setupIntentClientSecret}
                    >
                      <CreditCardForm
                        onSubmit={handleAddNewPaymentMethod}
                        isLoading={setupIntentLoading}
                        buttonText="Add Payment Method"
                        clientSecret={setupIntentClientSecret}
                      />
                    </StripeProvider>
                    <Button
                      variant="ghost"
                      onClick={() => setShowAddNew(false)}
                      w="full"
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </>
            )}
          </VStack>
        </ModalBody>

        {!showAddNew && (
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="brand.400"
              color="black"
              _hover={{ bg: 'brand.300' }}
              onClick={handleUpdateSubscriptionPaymentMethod}
              isLoading={isLoading}
              disabled={!selectedPaymentMethod}
            >
              Update Subscription
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
